// C1/C2 — the in-record entry point.
//
// Grant's directive + CLAUDE.md UX rule #4: the Cleanup System has TWO
// entry points that render the SAME engine — the centralized
// /cleanup workspace AND an in-record drawer reachable from a button
// in every record-detail page's fixed-chrome hero row. This provider
// owns that drawer. It is mounted ONCE in App.tsx; any detail page
// calls `useCleanup().openForRecord({ kind, id, label })`.
//
// The drawer deliberately re-hosts the exact workspace components —
// ResearchPane + DecideRail + ClusterMergeOverlay + CleanupRecordDrawer
// — driven by the same selectedId / refreshKey state machine, so the
// research → decide → preview → execute → undo experience is
// byte-identical to the workspace. The only difference is the issue
// source: GET /api/cleanup/for-record/:kind/:id instead of a lane.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ArrowRightLeft, Eye, Flag, Pencil } from "lucide-react";

import { api, ApiError, type CleanupIssueSummary } from "../api";
import { Drawer } from "../components/Drawer";
import type { RecordMoveTarget, RecordRef } from "./cleanup-records";
import { ResearchPane } from "./ResearchPane";
import { DecideRail } from "./DecideRail";
import { CleanupRecordDrawer } from "./CleanupRecordDrawer";
import { ClusterMergeOverlay } from "./ClusterMergeOverlay";
import { HouseholdClusterMergeOverlay } from "./HouseholdClusterMergeOverlay";
import { CleanupRealtyLinkOverlay } from "./CleanupRealtyLinkOverlay";
import { CleanupIvansCreatePolicyOverlay } from "./CleanupIvansCreatePolicyOverlay";
import { CleanupHouseholdSearchMergeOverlay } from "./CleanupHouseholdSearchMergeOverlay";
import { CleanupContactMoveOverlay } from "./CleanupContactMoveOverlay";
import { CleanupRecordMoveOverlay } from "./CleanupRecordMoveOverlay";
import { FlagProblem } from "./FlagProblem";
import { IssueCard } from "./IssueCard";
import { confidencePill, resolveFix } from "./fix-shape";
import { CleanupCoachmarks } from "./CleanupCoachmarks";
import { GlossaryTerm } from "./CleanupGlossaryTerm";
import type { InlineSaveResult } from "./CleanupInlineEdit";
import {
  CleanupToastHost,
  savedToast,
  resolvedToast,
  type CleanupToastMessage,
} from "./cleanup-toast";

export type CleanupRecordTarget = {
  kind: string;
  id: string;
  label: string;
  // Optional: fired whenever an issue is resolved in the drawer, so a
  // caller showing its own list (e.g. the Data Needed worklist) can
  // refetch and drop the cleared item without the user leaving the page.
  onResolved?: () => void;
  /**
   * The specific issue the CALLER believes is still open on this record
   * (Data Needed's "Work it" passes the worklist row's issue id).
   *
   * Brian Ream, 2026-08-31: "whenever I click on 'work it', it says there
   * is nothing to clean up on the record. So I'm not quite sure what to
   * do." The worklist is a SNAPSHOT, and the ~6h reconcile sweep resolves
   * items in large batches (production shows single-hour batches of 192,
   * 110, 80 and 77 on hh-no-primary alone). Any advisor whose page was
   * loaded before such a sweep is left holding a list of ghost rows: the
   * drawer correctly finds nothing, but answered with the cheerful
   * "Nothing to clean up on this record — nice", which reads as a broken
   * button rather than "this was already handled" — and the dead row
   * stayed on the page, so the next click did the same thing.
   *
   * When this is set and the record turns out to have no open issues, the
   * drawer says so in those words and fires `onResolved` so the host list
   * drops the stale row immediately (Cleanup UX rule 5: no dead ends).
   */
  expectedIssueId?: string;
};

type CleanupContextValue = {
  openForRecord: (target: CleanupRecordTarget) => void;
};

const CleanupContext = createContext<CleanupContextValue | null>(null);

// Read-only consumer hook. Throws if used outside the provider so a
// mis-mounted button fails loudly in dev rather than silently no-op.
export function useCleanup(): CleanupContextValue {
  const ctx = useContext(CleanupContext);
  if (!ctx) {
    throw new Error("useCleanup must be used within <CleanupProvider>");
  }
  return ctx;
}

type MergeClusterState = {
  members: RecordRef[];
  matchType: "email" | "phone" | "name_zip";
  matchValue: string;
};

const SECTION_LABEL: React.CSSProperties = {
  fontFamily: "var(--heading-font-web)",
  fontWeight: 800,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--navy)",
  marginBottom: 8,
};

// One row per open issue (docs/archive/mockups/cleanup-pane-redesign-v1.html): a
// short title, the RECORD it is about, and the confidence as a word. Two
// issues that ask the same question about two different loans used to
// read identically here because the row printed the question; now it
// prints the record, so they differ at a glance. Issues whose detector
// declares no fix keep the question as their title.
function IssueSelectorRow({
  issue,
  active,
  onClick,
}: {
  issue: CleanupIssueSummary;
  active: boolean;
  onClick: () => void;
}) {
  const fix = resolveFix(issue);
  const title = fix?.title ?? issue.decisionQuestion;
  const record =
    fix?.kind === "move-record"
      ? fix.record.label
      : (issue.affectedRecords[0]?.label ?? null);
  // Every shape that carries a confidence shows it, so the list says the
  // same thing the card will. (keep-one joined this on 2026-09-15 with
  // type-it-in — it had been falling through to no pill at all.)
  const pill =
    fix?.kind === "open-page"
      ? { label: "A step for you", tone: "info" as const }
      : fix && fix.kind !== "acknowledge"
        ? confidencePill(fix.confidence)
        : fix?.kind === "acknowledge"
          ? { label: "Heads up", tone: "info" as const }
          : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ic-row${active ? " is-sel" : ""}`}
      aria-current={active ? "true" : undefined}
    >
      <span className="ic-row-ico" aria-hidden>
        {fix?.kind === "acknowledge" ? (
          <Eye size={16} strokeWidth={1.5} />
        ) : fix?.kind === "type-it-in" ||
          fix?.kind === "type-it-in-fields" ||
          fix?.kind === "pick-one" ||
          fix?.kind === "add-policy" ||
          fix?.kind === "fill-one" ||
          fix?.kind === "same-person" ||
          fix?.kind === "choose-answer" ||
          fix?.kind === "same-household" ||
          fix?.kind === "find-household" ||
          fix?.kind === "retention" ||
          fix?.kind === "missing-documents" ? (
          <Pencil size={16} strokeWidth={1.5} />
        ) : (
          <ArrowRightLeft size={16} strokeWidth={1.5} />
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span className="ic-row-t" style={{ display: "block" }}>
          {title}
        </span>
        {record && (
          <span className="ic-row-s" style={{ display: "block" }}>
            {record}
          </span>
        )}
      </span>
      {pill ? (
        <span className={`ic-pill ic-pill--${pill.tone}`}>{pill.label}</span>
      ) : (
        <span style={{ fontSize: 11.5, color: "var(--fg-muted)" }}>
          {issue.confidence}%
        </span>
      )}
    </button>
  );
}

function CleanupRecordPanel({
  target,
  onClose,
}: {
  target: CleanupRecordTarget;
  onClose: () => void;
}) {
  const [issues, setIssues] = useState<CleanupIssueSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [drawerRecord, setDrawerRecord] = useState<RecordRef | null>(null);
  // Donna's inline edit: open the record drawer straight into its editor
  // (set by the decide rail's "Edit details…" affordance) vs the
  // read-only inspector (a plain record-name click).
  const [drawerStartEdit, setDrawerStartEdit] = useState(false);
  const [mergeCluster, setMergeCluster] =
    useState<MergeClusterState | null>(null);
  // Baker 2026-05-22 — parallel state for HOUSEHOLD-level merges. The
  // optional opts come from the couple-link path (Joy 2026-07-02).
  const [householdMergePair, setHouseholdMergePair] = useState<{
    refs: RecordRef[];
    defaultSurvivorId?: string;
    initialReason?: string;
    samePersonConfirmed?: boolean;
  } | null>(null);
  // Nate 2026-07-09 — realty-sr-needs-client-household: the picker that
  // links a synthetic-household realty SR to its client household. Wired
  // here (the in-record "Work It" drawer) so Data Needed → Work It offers
  // it, not only the /cleanup workspace.
  const [realtyLink, setRealtyLink] = useState<{
    issueId: string;
    serviceRecordLabel: string;
    decisionQuestion: string;
  } | null>(null);
  // Grant 2026-08-18 — "the carrier sent a policy we don't have": the
  // picker that creates it. Wired here (the in-record "Work It" drawer)
  // as well as in /cleanup, so the action exists wherever the item is
  // opened from — same reason the realty picker is wired in both.
  const [createPolicy, setCreatePolicy] = useState<{
    issueId: string;
    carrierLabel: string;
    decisionQuestion: string;
    first: string;
    last: string;
    policyType: string;
    household: { id: string; name: string } | null;
  } | null>(null);
  // Donna 2026-07-29 — the two resolution flows the workspace had and
  // this drawer did not. DecideRail renders their buttons only when the
  // matching callback is supplied, so in the drawer the recommendation
  // card described the action ("Find & merge into the spouse's
  // household") with NO control to run it — 273 open spouse-pass items,
  // plus hh-one-contact / hh-quarantined / trust-reattach, and
  // contact-no-household's move. Donna works from /data-needed, which
  // opens this drawer, so "there's no place to click merge like there
  // normally is" was exactly right: it exists only in /cleanup.
  const [householdSearchMerge, setHouseholdSearchMerge] = useState<{
    source: RecordRef;
    detectorSlug?: string;
  } | null>(null);
  const [contactMoveTarget, setContactMoveTarget] = useState<RecordRef | null>(
    null,
  );
  const [recordMoveNeeded, setRecordMoveNeeded] =
    useState<RecordMoveTarget | null>(null);
  // C3 — the "Flag a problem" form is available for ANY record,
  // including 0-issue records (service blueprint). On create we
  // refresh + select the new issue so the flagger immediately sees it
  // enter the same research/decide engine.
  const [flagOpen, setFlagOpen] = useState(false);
  // The card's "More options…" reveals the full decide rail beneath it
  // (assign, waiting-on-someone, notes, every bespoke flow). Per issue.
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    setMoreOpen(false);
  }, [selectedId]);
  // Fix #6 — feedback after an inline edit. This host owns the toast
  // (the drawer is told to suppress its own) so it can upgrade "Saved"
  // to "Resolved ✓" once the post-save reload shows the issue is gone —
  // no extra backend call, just watching the issue list refresh.
  const [toast, setToast] = useState<CleanupToastMessage | null>(null);
  // The (issue id, entity) we saved against; checked after the reload to
  // see whether that issue cleared. Null except in the window between a
  // save and its reload.
  const resolveWatch = useRef<{
    issueId: string;
    entity: "contact" | "household";
  } | null>(null);

  const onFlagCreated = (issueId: string) => {
    setFlagOpen(false);
    setSelectedId(issueId);
    setRefreshKey((k) => k + 1);
  };

  // An issue was resolved in the drawer: refresh the drawer's own issue
  // list AND tell the host (Data Needed) so its worklist drops the item.
  const notifyResolved = () => {
    setRefreshKey((k) => k + 1);
    target.onResolved?.();
  };

  // An inline edit saved in the record drawer. Show immediate "Saved"
  // feedback, then arm the resolve-watch so the reload below can upgrade
  // it to "Resolved ✓" if the selected issue clears. This is the ONLY
  // path that arms the watch — merges/decides/archives show their own
  // DecideRail confirmation, so they don't false-trigger a toast.
  const handleDrawerSaved = (result: InlineSaveResult) => {
    setToast(savedToast(result.entity, result.changed));
    resolveWatch.current = selectedId
      ? { issueId: selectedId, entity: result.entity }
      : null;
    notifyResolved();
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const r = await api.getCleanupIssuesForRecord(
          target.kind,
          target.id,
        );
        if (cancelled) return;
        setIssues(r.issues);
        // Keep the current selection if it's still present; otherwise
        // auto-select the first issue (UX rule #8: research auto-loads
        // — the steward shouldn't have to click twice).
        setSelectedId((prev) =>
          prev && r.issues.some((i) => i.id === prev)
            ? prev
            : (r.issues[0]?.id ?? null),
        );
        // Fix #6 — if this reload was triggered by an inline save and the
        // saved-against issue is now gone, the edit cleared it: upgrade
        // the "Saved" toast to an explicit "Resolved ✓".
        const watch = resolveWatch.current;
        if (watch) {
          resolveWatch.current = null;
          if (!r.issues.some((i) => i.id === watch.issueId)) {
            setToast(resolvedToast(watch.entity));
          }
        }
      } catch (e) {
        if (!cancelled) {
          // The transport layer already auto-retries a transient
          // session_unavailable blip (Mike's dead-end, 2026-07-23); if it
          // STILL surfaces here, the overload persisted past the retries —
          // give a plain-language message + a manual "Try again" (rendered
          // below) instead of the raw error.
          const persistentSessionBlip =
            e instanceof ApiError &&
            e.status === 503 &&
            e.code === "session_unavailable";
          setError(
            persistentSessionBlip
              ? "The system is briefly busy and couldn't load this record's cleanup items. Nothing was lost — try again in a moment."
              : e instanceof ApiError
                ? e.message
                : "Couldn't load cleanup issues for this record.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target.kind, target.id, refreshKey]);

  const selectedIssue = useMemo(
    () => issues.find((i) => i.id === selectedId) ?? null,
    [issues, selectedId],
  );
  // The fix the selected issue asks for — declared by its detector, or
  // inferred for rows stored before the declaration existed (fix-shape.ts).
  const selectedFix = useMemo(
    () => (selectedIssue ? resolveFix(selectedIssue) : null),
    [selectedIssue],
  );

  // The caller opened this drawer for an issue it believed was open, and
  // the record has none — the item cleared between the list being drawn
  // and this click (see `expectedIssueId`). Tell the host ONCE so its
  // list refetches and the ghost row disappears; the panel itself renders
  // the plain-language explanation below.
  //
  // Deliberately NOT notifyResolved(): that bumps refreshKey, which is
  // this drawer's own load trigger, and re-running the load on a
  // zero-issue record would re-fire this effect forever. The host's
  // refetch is the only thing needed here.
  const staleItemNotified = useRef(false);
  const alreadyCleared =
    !loading && !error && issues.length === 0 && !!target.expectedIssueId;
  useEffect(() => {
    if (!alreadyCleared || staleItemNotified.current) return;
    staleItemNotified.current = true;
    target.onResolved?.();
  }, [alreadyCleared, target]);

  return (
    <>
      <div
        style={{
          fontSize: 13,
          color: "var(--fg-3)",
          marginBottom: 14,
        }}
      >
        Issues on{" "}
        <b style={{ color: "var(--navy)" }}>{target.label}</b> — the same
        research &amp; decide engine as the{" "}
        <a
          href="/cleanup"
          style={{ color: "var(--blue)", fontWeight: 700 }}
        >
          Cleanup workspace
        </a>
        .{" "}
        <a
          href="/cleanup/help"
          style={{ color: "var(--blue)", fontWeight: 700 }}
        >
          Glossary
        </a>
        .
      </div>

      {/* C4 — first-run quick-start for the in-record drawer, once
          per user (persisted via the existing view-preferences
          store). Non-blocking. */}
      <CleanupCoachmarks variant="drawer" />

      {/* C3 — "Flag a problem" is ALWAYS available here, for any
          record, including 0-issue records (see the empty state
          below too). When open it focuses a compact form above the
          rest; on create the new manual issue enters the same engine
          and is auto-selected. */}
      {flagOpen ? (
        <div style={{ marginBottom: 16 }}>
          <FlagProblem
            entityKind={target.kind}
            entityId={target.id}
            entityLabel={target.label}
            onCreated={onFlagCreated}
            onCancel={() => setFlagOpen(false)}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 14,
          }}
        >
          <button
            onClick={() => setFlagOpen(true)}
            title="Flag a problem on this record for someone to research and resolve"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              border: "1px solid var(--mid-gray)",
              background: "var(--bg-0)",
              color: "var(--navy)",
              borderRadius: 8,
              padding: "7px 12px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            <Flag
              size={15}
              strokeWidth={1.75}
              color="var(--blue)"
              aria-hidden
            />
            Flag a problem
          </button>
        </div>
      )}

      {loading && (
        <div style={{ color: "var(--fg-muted)", fontSize: 14 }}>
          Loading cleanup issues…
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            border: "1px solid var(--danger)",
            background: "rgba(192,57,43,0.06)",
            color: "var(--navy)",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
          }}
        >
          <div>{error}</div>
          {/* No dead ends (UX rule #5): a manual retry is always
              available when the load failed. */}
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            style={{
              marginTop: 8,
              fontSize: 12.5,
              fontWeight: 700,
              padding: "5px 12px",
              borderRadius: 6,
              border: "1px solid var(--blue-dark)",
              background: "var(--blue)",
              color: "var(--white)",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && issues.length === 0 && (
        <div
          style={{
            border: "1px solid var(--mid-gray)",
            background: "var(--light-gray)",
            borderRadius: 8,
            padding: "16px 14px",
            fontSize: 13.5,
            color: "var(--fg-3)",
          }}
        >
          <div
            style={{
              fontWeight: 800,
              color: "var(--navy)",
              marginBottom: 4,
            }}
          >
            {alreadyCleared
              ? "This one's already taken care of."
              : "Nothing to clean up on this record — nice."}
          </div>
          {alreadyCleared ? (
            <>
              Nothing is left to do on {target.label} — it was resolved
              before you got here, either by someone else on the team or by
              the OS's own check confirming the data is now correct.{" "}
              <b>You don't need to do anything.</b> We've removed it from
              your list, so the next item is ready behind this panel.
            </>
          ) : (
            <>
              No open cleanup issues affect {target.label}. New issues appear
              here automatically as data changes (continuous detection runs
              on a schedule and on every edit).
            </>
          )}
        </div>
      )}

      {!loading && !error && issues.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={SECTION_LABEL}>
              {issues.length} open{" "}
              <GlossaryTerm k="issue">
                issue{issues.length === 1 ? "" : "s"}
              </GlossaryTerm>{" "}
              on this record
            </div>
            <div className="ic-list">
              {issues.map((i) => (
                <IssueSelectorRow
                  key={i.id}
                  issue={i}
                  active={i.id === selectedId}
                  onClick={() => setSelectedId(i.id)}
                />
              ))}
            </div>
          </div>

          {/* The card, when the detector declared (or the resolver can
              infer) the fix this issue asks for — one sentence, one
              picture, one button (docs/design/cleanup-pane-redesign-v1).
              The full research + decide engine stays underneath, revealed
              by "More options…", and remains the whole surface for an
              issue with no resolvable fix. */}
          {selectedIssue && selectedFix && (
            <IssueCard
              issue={selectedIssue}
              fix={selectedFix}
              onOpenRecord={(r) => {
                setDrawerStartEdit(false);
                setDrawerRecord(r);
              }}
              onResolved={notifyResolved}
              onToggleMore={() => setMoreOpen((m) => !m)}
              moreOpen={moreOpen}
              onRequestFieldMerge={(members, matchType, matchValue) =>
                setMergeCluster({ members, matchType, matchValue })
              }
              onRequestHouseholdFieldMerge={(members, opts) =>
                setHouseholdMergePair({ refs: members, ...opts })
              }
              onRequestHouseholdSearchMerge={(source) =>
                setHouseholdSearchMerge({
                  source,
                  ...(selectedIssue?.detectorSlug ? { detectorSlug: selectedIssue.detectorSlug } : {}),
                })
              }
              onRequestContactMove={(source) => setContactMoveTarget(source)}
              onRequestRealtyLink={(source) =>
                setRealtyLink({
                  issueId: selectedIssue.id,
                  serviceRecordLabel: source.label,
                  decisionQuestion: selectedIssue.decisionQuestion,
                })
              }
              onRequestCreatePolicy={(source, suggested) =>
                setCreatePolicy({
                  issueId: selectedIssue.id,
                  carrierLabel: source.label,
                  decisionQuestion: selectedIssue.decisionQuestion,
                  first: suggested.first,
                  last: suggested.last,
                  policyType: suggested.policyType,
                  household: suggested.household,
                })
              }
            />
          )}
          {(!selectedFix || moreOpen) && (
          <div
            style={{
              border: "1px solid var(--mid-gray)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <ResearchPane
              issueId={selectedId}
              onOpenRecord={(r) => {
                setDrawerStartEdit(false);
                setDrawerRecord(r);
              }}
            />
            <div
              style={{ borderTop: "1px solid var(--mid-gray)" }}
            />
            <DecideRail
              issue={selectedIssue}
              onResolved={notifyResolved}
              onRequestFieldMerge={(members, matchType, matchValue) =>
                setMergeCluster({ members, matchType, matchValue })
              }
              onRequestHouseholdFieldMerge={(members, opts) =>
                setHouseholdMergePair({ refs: members, ...opts })
              }
              onRequestHouseholdSearchMerge={(source) =>
                setHouseholdSearchMerge({
                  source,
                  ...(selectedIssue?.detectorSlug
                    ? { detectorSlug: selectedIssue.detectorSlug }
                    : {}),
                })
              }
              onRequestContactMove={(source) => setContactMoveTarget(source)}
              onRequestRecordMove={(target) => setRecordMoveNeeded(target)}
              onRequestRealtyLink={(source) => {
                if (!selectedIssue) return;
                setRealtyLink({
                  issueId: selectedIssue.id,
                  serviceRecordLabel: source.label,
                  decisionQuestion: selectedIssue.decisionQuestion,
                });
              }}
              onRequestCreatePolicy={(source, suggested) => {
                if (!selectedIssue) return;
                setCreatePolicy({
                  issueId: selectedIssue.id,
                  carrierLabel: source.label,
                  decisionQuestion: selectedIssue.decisionQuestion,
                  first: suggested.first,
                  last: suggested.last,
                  policyType: suggested.policyType,
                  household: suggested.household,
                });
              }}
              onRequestEdit={(r) => {
                setDrawerStartEdit(true);
                setDrawerRecord(r);
              }}
            />
          </div>
          )}
        </div>
      )}

      {/* Record inspector — same overlay the workspace uses. Renders
          above this drawer (its own fixed overlay / higher z-index). */}
      {drawerRecord && (
        <CleanupRecordDrawer
          record={drawerRecord}
          startInEdit={drawerStartEdit}
          onSaved={handleDrawerSaved}
          suppressSaveToast
          onClose={() => {
            setDrawerRecord(null);
            setDrawerStartEdit(false);
          }}
          {...(selectedIssue
            ? { issueRecords: selectedIssue.affectedRecords }
            : {})}
          {...(selectedId
            ? {
                onArchive: async (r: RecordRef) => {
                  await api.decideCleanupIssue({
                    issue_id: selectedId,
                    verb: "archive",
                    overrode_recommendation: true,
                    params: { archive_id: r.id, archive_kind: r.kind },
                  });
                  setDrawerRecord(null);
                  notifyResolved();
                },
              }
            : {})}
        />
      )}

      {/* Field-level / cluster merge — same overlay + same
          mark-resolved-on-merge flow as the workspace. */}
      {mergeCluster && (
        <ClusterMergeOverlay
          members={mergeCluster.members}
          matchType={mergeCluster.matchType}
          matchValue={mergeCluster.matchValue}
          onClose={() => setMergeCluster(null)}
          onMerged={() => {
            const sid = selectedId;
            void (async () => {
              if (sid) {
                try {
                  await api.markCleanupIssueResolved(sid);
                } catch {
                  /* ~6h sweep will auto-resolve; don't block the UI */
                }
              }
              setMergeCluster(null);
              notifyResolved();
            })();
          }}
        />
      )}

      {/* HOUSEHOLD merge overlay — same mark-resolved-on-merge flow. */}
      {householdMergePair && (
        <HouseholdClusterMergeOverlay
          refs={householdMergePair.refs}
          {...(householdMergePair.defaultSurvivorId
            ? { defaultSurvivorId: householdMergePair.defaultSurvivorId }
            : {})}
          {...(householdMergePair.initialReason
            ? { initialReason: householdMergePair.initialReason }
            : {})}
          {...(householdMergePair.samePersonConfirmed
            ? { samePersonConfirmed: true }
            : {})}
          onClose={() => setHouseholdMergePair(null)}
          onMerged={() => {
            const sid = selectedId;
            void (async () => {
              if (sid) {
                try {
                  await api.markCleanupIssueResolved(sid);
                } catch {
                  /* ~6h sweep will auto-resolve; don't block the UI */
                }
              }
              setHouseholdMergePair(null);
              notifyResolved();
            })();
          }}
        />
      )}

      {/* Search for the destination household, then merge — spouse-pass
          ("Find & merge into the spouse's household"), hh-one-contact,
          hh-quarantined, trust-reattach. Same mark-resolved-on-merge flow
          as the known-pair overlay above. (Donna 2026-07-29.) */}
      {householdSearchMerge && (
        <CleanupHouseholdSearchMergeOverlay
          source={householdSearchMerge.source}
          {...(householdSearchMerge.detectorSlug
            ? { detectorSlug: householdSearchMerge.detectorSlug }
            : {})}
          onClose={() => setHouseholdSearchMerge(null)}
          onMerged={() => {
            const sid = selectedId;
            void (async () => {
              if (sid) {
                try {
                  await api.markCleanupIssueResolved(sid);
                } catch {
                  /* ~6h sweep will auto-resolve; don't block the UI */
                }
              }
              setHouseholdSearchMerge(null);
              notifyResolved();
            })();
          }}
        />
      )}

      {/* Find a household and move the contact into it —
          contact-no-household's `move` recommendation. */}
      {/* Find the right family and move the record onto it — the
          `move-household` recommendation, and the no-recommendation
          "which family is this account?" question. The modal owns the
          reason picker, the POST and the 60s undo toast; the issue is
          marked resolved here, same as the contact move below. */}
      {recordMoveNeeded && (
        <CleanupRecordMoveOverlay
          target={recordMoveNeeded}
          onClose={() => setRecordMoveNeeded(null)}
          onMoved={() => {
            const sid = selectedId;
            void (async () => {
              if (sid) {
                try {
                  await api.markCleanupIssueResolved(sid);
                } catch {
                  /* ~6h sweep will auto-resolve; don't block the UI */
                }
              }
              setRecordMoveNeeded(null);
              notifyResolved();
            })();
          }}
        />
      )}

      {contactMoveTarget && (
        <CleanupContactMoveOverlay
          source={contactMoveTarget}
          onClose={() => setContactMoveTarget(null)}
          onMoved={() => {
            const sid = selectedId;
            void (async () => {
              if (sid) {
                try {
                  await api.markCleanupIssueResolved(sid);
                } catch {
                  /* ~6h sweep will auto-resolve; don't block the UI */
                }
              }
              setContactMoveTarget(null);
              notifyResolved();
            })();
          }}
        />
      )}

      {/* Realty SR → client-household picker (the overlay behind the
          "Pick the client household…" button). The realty `match`
          handler resolves the issue server-side; just close + refresh. */}
      {createPolicy && (
        <CleanupIvansCreatePolicyOverlay
          issueId={createPolicy.issueId}
          carrierLabel={createPolicy.carrierLabel}
          decisionQuestion={createPolicy.decisionQuestion}
          suggestedFirstName={createPolicy.first}
          suggestedLastName={createPolicy.last}
          suggestedPolicyType={createPolicy.policyType}
          suggestedHousehold={createPolicy.household}
          onClose={() => setCreatePolicy(null)}
          onCreated={() => {
            setCreatePolicy(null);
            notifyResolved();
          }}
        />
      )}

      {realtyLink && (
        <CleanupRealtyLinkOverlay
          issueId={realtyLink.issueId}
          serviceRecordLabel={realtyLink.serviceRecordLabel}
          decisionQuestion={realtyLink.decisionQuestion}
          onClose={() => setRealtyLink(null)}
          onLinked={() => {
            setRealtyLink(null);
            notifyResolved();
          }}
        />
      )}

      <div
        style={{
          marginTop: 18,
          paddingTop: 12,
          borderTop: "1px solid var(--mid-gray)",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          onClick={onClose}
          style={{
            border: "1px solid var(--mid-gray)",
            background: "var(--bg-0)",
            color: "var(--navy)",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>

      <CleanupToastHost toast={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

export function CleanupProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<CleanupRecordTarget | null>(null);

  const openForRecord = useCallback((t: CleanupRecordTarget) => {
    setTarget(t);
  }, []);

  const value = useMemo<CleanupContextValue>(
    () => ({ openForRecord }),
    [openForRecord],
  );

  return (
    <CleanupContext.Provider value={value}>
      {children}
      <Drawer
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Cleanup"
        width={760}
      >
        {target && (
          <CleanupRecordPanel
            // Remount the panel per target so its internal state
            // (selection, refreshKey) resets cleanly between records.
            key={`${target.kind}:${target.id}`}
            target={target}
            onClose={() => setTarget(null)}
          />
        )}
      </Drawer>
    </CleanupContext.Provider>
  );
}
